import { Box } from "@mui/material";
import { useSelector } from "react-redux";

import {
  Autocomplete,
  Button,
  Divider,
  IconButton,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Typography,
} from "@mui/joy";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Talent_Entity } from "../../entities/talent";
import {
  formOpenSelector,
  selectedTalentID,
  setFormOpen,
  setSelectedTalentID,
} from "../../redux/filmsReducer";
import { usePositions } from "../../redux/position/positionHooks";
import {
  useCreateTalentMutation,
  useDeleteTalentMutation,
  useUpdateTalentMutation,
} from "../../redux/talent/talentEndpoints";
import { useTalent } from "../../redux/talent/talentHooks";
import { useAskQuestion } from "../askQuestion/askQuestion";

/**
 *
 * @returns {ReactElement} FormTalent page
 */
export function FormTalent() {
  const talentID = useSelector(selectedTalentID);
  const { talent } = useTalent(talentID);
  const open = useSelector(formOpenSelector("talent"));
  const [_talent, setTalent] = useState<Partial<Talent_Entity>>({
    archived: false,
    active: true,
  });
  const [showSSN, setShowSSN] = useState(true);
  const [showEIN, setShowEIN] = useState(true);

  const [createTalent, { isLoading: isCreating }] = useCreateTalentMutation();
  const [updateTalent, { isLoading: isUpdating }] = useUpdateTalentMutation();
  const [deleteTalent] = useDeleteTalentMutation();

  const { positions } = usePositions();

  const askQuestion = useAskQuestion();

  const dispatch = useDispatch();

  useEffect(() => {
    if (talent?.id) {
      setTalent(talent);
    }
  }, [talent]);

  const onClose = () => {
    dispatch(setFormOpen({ isOpen: false, formID: "talent" }));
  };

  const save = () => {
    if (talentID && _talent) {
      updateTalent({ id: talentID, body: _talent });
    } else if (_talent) {
      createTalent(_talent)
        .unwrap()
        .then((e) => {
          dispatch(setSelectedTalentID(e.id));
        });
    }
    onClose();
  };

  const askDelete = () => {
    askQuestion("Are you sure?", ["Cancel", "Yes"], {
      subtitle: (
        <Typography>
          You are about to delete a Talent. Please confirm this action.
        </Typography>
      ),
    }).then((i) => {
      if (i == 1) confirmDelete();
    });
  };

  const confirmDelete = async () => {
    if (talentID) deleteTalent(talentID);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog layout="center" sx={{ width: 750, maxWidth: "95vw" }}>
        <ModalClose onClick={onClose} />
        <Typography
          startDecorator={<i className="fa fa-user-music" />}
          level="h6"
          sx={{ fontWeight: "bold" }}
        >
          {talentID ? "Edit" : "Create"} Talent
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {_talent ? (
          <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}
            >
              <Typography>Personal Information</Typography>
              <Input
                startDecorator="First Name:"
                value={_talent.firstName ?? ""}
                onChange={(e) =>
                  setTalent((v) => ({ ...v, firstName: e.target.value }))
                }
                type="text"
                autoFocus
              />
              <Input
                startDecorator="Last Name:"
                value={_talent.lastName ?? ""}
                onChange={(e) =>
                  setTalent((v) => ({ ...v, lastName: e.target.value }))
                }
                type="text"
              />
              <Input
                startDecorator="Code:"
                value={_talent.code ?? ""}
                onChange={(e) =>
                  setTalent((v) => ({ ...v, code: e.target.value }))
                }
                type="text"
              />
              <Autocomplete
                key={_talent.positionID}
                startDecorator="Position:"
                options={positions ?? []}
                value={
                  positions?.find((p) => p.id === _talent.positionID) ?? null
                }
                getOptionLabel={(option) => option?.name ?? ""}
                isOptionEqualToValue={(option, value) =>
                  option.id === value?.id
                }
                slotProps={{
                  listbox: { sx: { zIndex: 9999 } },
                }}
                onChange={(_, value) =>
                  setTalent((v) => ({
                    ...v,
                    positionID: value?.id ?? undefined,
                  }))
                }
              />
              <Input
                startDecorator="Birthdate:"
                value={_talent.birthDate ?? ""}
                onChange={(e) =>
                  setTalent((v) => ({ ...v, birthDate: e.target.value }))
                }
                type="date"
              />
              <Input
                startDecorator="Email:"
                value={_talent.email ?? ""}
                onChange={(e) =>
                  setTalent((v) => ({ ...v, email: e.target.value }))
                }
                type="email"
              />
              <Input
                startDecorator="Phone:"
                value={_talent.phone ?? ""}
                onChange={(e) =>
                  setTalent((v) => ({ ...v, phone: e.target.value }))
                }
                type="text"
                placeholder="(123) 456-7890"
              />
              <Input
                startDecorator="Dropbox:"
                value={_talent.dropbox ?? ""}
                onChange={(e) =>
                  setTalent((v) => ({ ...v, dropbox: e.target.value }))
                }
                type="text"
              />
              <Input
                startDecorator="Google:"
                value={_talent.google ?? ""}
                onChange={(e) =>
                  setTalent((v) => ({ ...v, google: e.target.value }))
                }
                type="text"
              />
            </Box>
            <Divider orientation="vertical" />
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}
            >
              <Typography>Address</Typography>
              <Input
                startDecorator="Tax Status:"
                value={_talent.taxStatus ?? ""}
                onChange={(e) =>
                  setTalent((v) => ({ ...v, taxStatus: e.target.value }))
                }
                type="text"
              />
              <Input
                startDecorator="Address:"
                value={_talent.address ?? ""}
                onChange={(e) =>
                  setTalent((v) => ({ ...v, address: e.target.value }))
                }
                type="text"
              />
              <Input
                startDecorator="City:"
                value={_talent.city ?? ""}
                onChange={(e) =>
                  setTalent((v) => ({ ...v, city: e.target.value }))
                }
                type="text"
              />
              <Input
                startDecorator="Zip Code:"
                value={_talent.zipcode ?? ""}
                onChange={(e) =>
                  setTalent((v) => ({ ...v, zipcode: e.target.value }))
                }
                type="text"
              />
              <Input
                startDecorator="State:"
                value={_talent.state ?? ""}
                onChange={(e) =>
                  setTalent((v) => ({ ...v, state: e.target.value }))
                }
                type="text"
              />
              <Typography>Identification</Typography>
              <Input
                slotProps={{ input: { style: { textAlign: "right" } } }}
                startDecorator={"SSN:"}
                fullWidth
                onBlur={() => {
                  setTalent((m) => ({ ...m, ssn: formatSSN(m.ssn ?? "") }));
                }}
                value={_talent.ssn || ""}
                onChange={(e) =>
                  setTalent((m) => ({ ...m, ssn: e.target.value }))
                }
                type={showSSN ? "text" : "password"}
                endDecorator={
                  <IconButton
                    tabIndex={-1}
                    onClick={() => setShowSSN((s) => !s)}
                    size="sm"
                  >
                    <i
                      className={showSSN ? "fas fa-eye-slash" : "fas fa-eye"}
                    />
                  </IconButton>
                }
              />
              <Input
                slotProps={{ input: { style: { textAlign: "right" } } }}
                startDecorator={"EIN:"}
                fullWidth
                onBlur={() => {
                  setTalent((m) => ({ ...m, ein: formatEIN(m.ein ?? "") }));
                }}
                value={_talent.ein || ""}
                onChange={(e) =>
                  setTalent((m) => ({ ...m, ein: e.target.value }))
                }
                type={showEIN ? "text" : "password"}
                endDecorator={
                  <IconButton
                    tabIndex={-1}
                    onClick={() => setShowEIN((s) => !s)}
                    size="sm"
                  >
                    <i
                      className={showEIN ? "fas fa-eye-slash" : "fas fa-eye"}
                    />
                  </IconButton>
                }
              />
            </Box>
          </Box>
        ) : (
          []
        )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {talentID ? (
          <Button variant="soft" onClick={askDelete} color="danger">
            Delete
          </Button>
        ) : (
          []
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose} color="neutral" variant="soft">
          Cancel
        </Button>
        <Button loading={isCreating || isUpdating} onClick={save}>
          {talentID ? "Save" : "Create"}
        </Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
}

function formatEIN(input: string) {
  try {
    // Remove all non-digit characters
    const digits = input.replace(/\D/g, "");

    // Ensure there are exactly 9 digits
    if (digits.length !== 9) {
      throw new Error("Invalid EIN: must contain exactly 9 digits.");
    }

    // Format as XX-XXXXXXX
    return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  } catch (error) {
    return "";
  }
}

function formatSSN(input: string) {
  try {
    // Remove all non-digit characters
    const digits = input.replace(/\D/g, "");

    // Ensure there are exactly 9 digits
    if (digits.length !== 9) {
      throw new Error("Invalid SSN: must contain exactly 9 digits.");
    }

    // Format as XXX-XX-XXXX
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  } catch (error) {
    return "";
  }
}
